/**
 * SchedulesEditor.tsx — FR-013 (H4) / FR-014 (Twin #1) / T314 rewrite.
 *
 * Bindings:
 *   - api.schedules.markAsReady      — REAL twin (convex/schedules.ts:421)
 *   - api.schedules.duplicateSchedule — REAL (orphan #3, plan §3 Wave 3h)
 *   - api.schedules.scheduleForFuture — REAL (orphan #4, plan §3 Wave 3h)
 *   - api.schedules.getScheduleWithItems — REAL query (convex/schedules.ts:76)
 *
 * The stub-side namesake `api.schedule.markAsReady` (singular, at
 * convex/schedule.ts:1098) is intentionally NOT referenced anywhere
 * in this file — see specs/exploration/stub_orphan_inventory.md §4
 * Twin #1, and the negative assertion in
 * frontend-v3/tests/destub/twins.test.ts (T-C).
 *
 * Every mutation call site is routed through `useMutationWithToast`
 * (FR-013 (H1)): the wrapper logs argument KEYS only (never values),
 * re-throws after toasting, and never reports a false success.
 *
 * H4 ratchet: this file MUST NOT be a thin re-export shell. See
 * frontend-v3/tests/destub/honest-state.test.ts (H4) and the sibling
 * OrgSettings.tsx for the matching twin rewrite.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'convex/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  AccessGate,
  Breadcrumbs,
  Btn,
  ConfirmModal,
  EmptyState,
  Field,
  Icon,
  Input,
  Modal,
  Panel,
  Pill,
  Select,
  Skeleton,
  StatCard,
} from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { navigate } from '../../lib/nav';
import { useChannelList } from '../../hooks/useChannelList';
import { ScheduleValidationDrawer } from '../schedule/ScheduleValidationDrawer';
import { AddContentSidebar } from './AddContentSidebar';
import { FormatMismatchCell } from './FormatMismatchCell';
import { api } from '@convex/api';
// T318 — the mock fixture import retired; the channel select binds to the
// real `useChannelList()` snapshot and renders an honest empty option
// list while the subscription resolves. No fixture dependency remains.
import { fmtDur } from '../../lib/format';

interface SchedulesEditorProps {
  id?: string;
}

// Virtualized-timeline row pitch, in px. The virtualizer never calls
// `measureElement`, so this value IS the row pitch — every row is absolutely
// positioned at `translateY(index * TIMELINE_ROW_H)`. To keep the estimate
// honest the row box also sets `height: TIMELINE_ROW_H` with
// `boxSizing: 'border-box'`, so measured height can never exceed the pitch.
// Budget check (50 − 8px padding × 2 − 1px separator = 33px of content):
// Pill size="sm" ≈ 23px, Btn size="sm" (FormatMismatchCell) ≈ 28px, and the
// AccessGate resolving Skeleton h={32} — the tallest child — all fit.
const TIMELINE_ROW_H = 50;

export function SchedulesEditor({ id }: SchedulesEditorProps) {
  // ──────────────────────────────────────────────────────────────────
  // Real Convex query — the container + its items.
  // ──────────────────────────────────────────────────────────────────
  // The route binds `:id` to the Convex `schedules` row id; we only
  // call the query when the id is present AND plausibly well-formed.
  // A malformed id (the prototype's `sc_new` placeholder from "Nueva
  // programación", or a hand-typed URL) would fail `v.id("schedules")`
  // and throw an ArgumentValidationError straight past the error
  // boundary — guard it and fall through to the honest states below.
  const validId = !!id && /^[a-z0-9]{20,}$/.test(id);
  const scheduleDoc = useQuery(
    api.schedules.getScheduleWithItems,
    validId ? { scheduleId: id as any } : 'skip',
  );

  // NMW-416 — schedule profile-consistency (resolution/fps/audio) validation.
  // Surfaced two ways on this page: an always-on inline banner (below) that
  // appears the moment an outlier exists, and the full ScheduleValidationDrawer
  // (opened via "Validar"). Same well-formed-id guard as the doc query.
  const validation = useQuery(
    api.compliance.checkSchedule,
    validId ? { scheduleId: id as any } : 'skip',
  );

  // T318 — wired channel list for the "Asignar a canal" select. Returns
  // `undefined` while loading (honest: select shows no options yet); never
  // fabricated. The real Convex `_id` is the option value so future-schedule
  // submissions carry a real id, not a mock string.
  const wiredChannels = useChannelList();

  // ──────────────────────────────────────────────────────────────────
  // Real mutations — every one wrapped, every one fail-loud on error.
  // ──────────────────────────────────────────────────────────────────
  const markAsReadyM = useMutationWithToast(api.schedules.markAsReady, {
    logLabel: 'schedules_markAsReady',
    successMessage: 'Programación marcada como lista.',
    errorMessagePrefix: 'No se pudo marcar como lista',
  });
  const duplicateScheduleM = useMutationWithToast(
    api.schedules.duplicateSchedule,
    {
      logLabel: 'schedules_duplicateSchedule',
      successMessage: 'Programación duplicada.',
      errorMessagePrefix: 'No se pudo duplicar la programación',
    },
  );
  const scheduleForFutureM = useMutationWithToast(
    api.schedules.scheduleForFuture,
    {
      logLabel: 'schedules_scheduleForFuture',
      successMessage: 'Programación agendada.',
      errorMessagePrefix: 'No se pudo agendar la programación',
    },
  );
  // "Publicar ahora" — sets the schedule active, stamps channelId on items,
  // and starts the channel workflow (runAfter startChannelWorkflow). The VOD
  // "salir al aire" path; no epoch needed. Honest wrapper surfaces real errors.
  const publishImmediatelyM = useMutationWithToast(
    api.schedules.publishImmediately,
    {
      logLabel: 'schedules_publishImmediately',
      successMessage: 'Programación publicada. Saliendo al aire…',
      errorMessagePrefix: 'No se pudo publicar la programación',
    },
  );
  // A schedule with no channel can't accept items (addScheduleItemToContainer
  // requires container.channelId). Operators can assign one here.
  const assignChannelM = useMutationWithToast(
    api.schedules.assignToChannel,
    {
      logLabel: 'schedules_assignToChannel',
      successMessage: 'Canal asignado.',
      errorMessagePrefix: 'No se pudo asignar el canal',
    },
  );

  // ──────────────────────────────────────────────────────────────────
  // Item authoring mutations (issue #13 — "Añadir contenido" + per-item
  // delete). Each is routed through the honest-error wrapper. After any
  // structural change we call `recalculateContainerStartTimes` so the
  // timeline's startTime/endTime stay contiguous.
  // ──────────────────────────────────────────────────────────────────
  const addItemM = useMutationWithToast(
    api.schedule.addScheduleItemToContainer,
    {
      logLabel: 'schedule_addScheduleItemToContainer',
      successMessage: 'Contenido añadido a la programación.',
      errorMessagePrefix: 'No se pudo añadir el contenido',
    },
  );
  const deleteItemM = useMutationWithToast(
    api.schedule.deleteScheduleItemFromContainer,
    {
      logLabel: 'schedule_deleteScheduleItemFromContainer',
      successMessage: 'Elemento eliminado.',
      errorMessagePrefix: 'No se pudo eliminar el elemento',
    },
  );
  const recalcM = useMutationWithToast(
    api.schedule.recalculateContainerStartTimes,
    {
      logLabel: 'schedule_recalculateContainerStartTimes',
      // Silent on success — it's a follow-up to add/delete, not a
      // user-initiated action; the add/delete toast already fired.
      errorMessagePrefix: 'No se pudieron recalcular los horarios',
    },
  );
  // Reorder (issue #?? — drag-and-drop sequence editing). Persists the new
  // order of item ids; the backend rewrites every sequenceNumber, then we
  // recalc start/end times so the timeline stays contiguous. Silent on
  // success — the reorder is its own visible feedback (the row moved).
  const reorderM = useMutationWithToast(
    api.schedule.reorderScheduleItems,
    {
      logLabel: 'schedule_reorderScheduleItems',
      errorMessagePrefix: 'No se pudo reordenar la programación',
    },
  );
  // NMW-418 Phase 4 — repin a schedule item to a DIFFERENT ready rendition of
  // the SAME asset. The backend recalcs downstream start times itself, so NO
  // client-side recalcM follow-up (unlike add/delete/reorder). Honest wrapper
  // surfaces belongs-to-asset / not-ready / no-playbackUrl throws as an error
  // toast — never a fabricated success.
  const repinM = useMutationWithToast(api.schedule.repinScheduleItem, {
    logLabel: 'schedule_repinScheduleItem',
    successMessage: 'Versión actualizada.',
    errorMessagePrefix: 'No se pudo cambiar la versión',
  });

  // ──────────────────────────────────────────────────────────────────
  // Local UI state (modal, channel selection for publish dialog).
  // ──────────────────────────────────────────────────────────────────
  const [pubOpen, setPubOpen] = useState(false);
  // NMW-416 — validation drawer open state ("Validar" / "Ver detalles").
  // NMW-452 — `?validate=1` opens it on arrival, so the /schedules list badge
  // lands the operator on the detail they clicked for instead of the bare
  // editor. Read once on mount (lazy initializer); the param is not tracked.
  const [valOpen, setValOpen] = useState(
    () =>
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('validate') === '1',
  );
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  // datetime-local string (e.g. "2026-06-19T15:30"); converted to epoch ms on
  // submit. `scheduleLater` gates the future-scheduling UI — default is publish
  // now ("salir al aire"), the common case.
  const [futureDate, setFutureDate] = useState<string>('');
  const [scheduleLater, setScheduleLater] = useState(false);
  // Channel chosen in the "assign a channel" banner (shown when the schedule
  // has no channel — adding items requires one).
  const [assignChannelId, setAssignChannelId] = useState('');

  // Pre-fill the publish dialog's channel with the schedule's already-assigned
  // channel — operators shouldn't have to re-pick it. `prev || …` so a manual
  // change still sticks.
  useEffect(() => {
    const cid = (scheduleDoc as { channelId?: string } | null | undefined)?.channelId;
    if (cid) setSelectedChannel((prev) => prev || cid);
  }, [scheduleDoc]);

  // Content sidebar (replaces the old asset-picker dialog) + the asset id
  // picked (we read its title/durationMs/playbackUrl via getAssetById before
  // building the item). The sidebar stays open across adds, so several
  // elements can be appended one button-click at a time.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedAssetId, setPickedAssetId] = useState<string | null>(null);
  const pickedAsset = useQuery(
    api.assetQueries.getAssetById,
    pickedAssetId && /^[a-z0-9]{20,}$/.test(pickedAssetId)
      ? { assetId: pickedAssetId as any }
      : 'skip',
  );

  // Per-item delete confirmation.
  const [deleteTarget, setDeleteTarget] = useState<{ _id: string; title: string } | null>(null);

  // NMW-418 Phase 4 — rendition picker. Holds the item whose version-picker
  // Modal is open (null = closed). Scoping the getAssetTranscodes query to
  // this single object guarantees ONE query at a time (one-open-at-a-time).
  const [versionTarget, setVersionTarget] = useState<{
    _id: string;
    assetId: string;
    transcodeId: string | null | undefined;
    title: string;
  } | null>(null);
  // Scoped to the open picker's asset only — 'skip' whenever the Modal is
  // closed (versionTarget === null) or the assetId is not a well-formed id.
  const versionData = useQuery(
    api.transcodes.getAssetTranscodes,
    versionTarget && /^[a-z0-9]{20,}$/.test(versionTarget.assetId)
      ? { assetId: versionTarget.assetId as any }
      : 'skip',
  );

  // Reorder mode (opt-in). `localOrder` is the optimistic working order of
  // item ids while a drag is being persisted — it absorbs the gap between
  // drop and the query re-resolving with the new sequenceNumbers, so rows
  // don't snap back. `null` means "trust the query order" (not reordering).
  const [reorderMode, setReorderMode] = useState(false);
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);

  // ──────────────────────────────────────────────────────────────────
  // Virtualization (NFR-004): windows the timeline list once the row
  // count crosses ~300. The virtualizer ref is bound to the scroll
  // parent below; estimateSize is `TIMELINE_ROW_H` (see the constant at the
  // top of this file for the geometry and the budget check) and the row box
  // pins itself to that exact height, so estimate and reality agree. The
  // overscan keeps a small buffer above/below the visible window so fast
  // scrolling doesn't show a blank gap.
  // ──────────────────────────────────────────────────────────────────
  const timelineScrollRef = useRef<HTMLDivElement | null>(null);

  // Derived view data — computed with optional chaining so it is safe to
  // evaluate BEFORE the loading/empty returns (items = [] until the doc
  // resolves). Keeping it here lets the virtualizer hook run unconditionally.
  const name: string = scheduleDoc?.name ?? 'Nueva programación';
  const items: any[] = scheduleDoc?.items ?? [];
  const totalMs: number = items.reduce(
    (sum: number, it: any) => sum + (it?.durationMs ?? 0),
    0,
  );
  // NMW-416 — profile-consistency outliers surfaced by compliance.checkSchedule.
  // Each carries `outlier.{scheduleItemId,title}` so the banner's "Quitar" can
  // reuse the existing per-item delete-confirm flow (setDeleteTarget).
  const profileWarnings: any[] = (validation?.rules ?? []).filter(
    (r: any) =>
      r?.type === 'profile_consistency' && r?.status === 'warning' && r?.outlier,
  );
  // NMW-452 — the header's "Validar (n)" counts EVERY non-pass rule, exactly
  // like the /schedules badge and the drawer's own list. Counting only the
  // profile outliers here made the same schedule read "3 avisos" in the list
  // and "Validar (1)" one click later. `profileWarnings` stays type-filtered
  // because `outlierById` below dereferences `r.outlier`.
  const validationIssueCount: number = (validation?.rules ?? []).filter(
    (r: any) => r?.status === 'warning' || r?.status === 'fail',
  ).length;
  // Schedule-row id → its profile-consistency rule. Used to tint the per-row
  // resolution/fps pill AND to show an inline warning (with an explanatory
  // tooltip) on each timeline element that is an outlier.
  const outlierById = new Map<string, any>(
    profileWarnings.map((r: any) => [r.outlier.scheduleItemId as string, r]),
  );
  // Human-readable reason for a row's mismatch, shown as the warning's tooltip.
  const warnReasonFor = (itemId: string): string | null => {
    const r = outlierById.get(itemId);
    if (!r) return null;
    return (
      [r.description, r.details].filter(Boolean).join(' — ') ||
      'Perfil de video inconsistente'
    );
  };
  // Compact "HxW@fps" label for a timeline row (e.g. "720x1280@30"). Honest
  // about missing data: assets not yet re-probed for fps show "@null".
  const fmtProfile = (it: any): string => {
    if (!it?.resolution) return 'Sin perfil';
    const fps = typeof it?.frameRate === 'number' ? it.frameRate : 'null';
    return `${it.resolution.height}x${it.resolution.width}@${fps}`;
  };
  // NMW-418 Phase 4 — "HxW@fps" for a rendition profile, matching the row
  // pill's height×width order. Honest about missing data.
  const fmtRendition = (p: any): string => {
    if (!p || (p.height == null && p.width == null)) {
      return p?.label ? String(p.label) : 'Sin perfil';
    }
    const fps = typeof p?.frameRate === 'number' ? p.frameRate : 'null';
    return `${p.height ?? '?'}x${p.width ?? '?'}@${fps}`;
  };
  const KIND_LABEL: Record<string, string> = {
    original: 'original',
    reencode: 'reencodificado',
    variant: 'variante',
  };

  // NFR-004 virtualizer — MUST run before any early return (Rules of Hooks).
  // It previously sat AFTER the skeleton/empty returns, so once a schedule
  // resolved the hook count changed → "rendered more hooks than during the
  // previous render" crash. initialRect gives a sane window for first paint /
  // jsdom where getBoundingClientRect() reports zero height.
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => timelineScrollRef.current,
    // The virtualizer never measures rows, so this estimate IS the row pitch.
    // The row box below pins itself to the same constant, which is sized for
    // the TALLEST row (AccessGate's resolving Skeleton, then a
    // FormatMismatchCell button) — see TIMELINE_ROW_H at the top of the file.
    estimateSize: () => TIMELINE_ROW_H,
    overscan: 8,
    initialRect: { width: 800, height: 480 },
  });
  const virtualRows = rowVirtualizer.getVirtualItems();

  // Once the picked asset resolves, build the schedule item from its real
  // fields and append it, then recalc start times. A ref guards against the
  // effect re-firing while the query result is stable (it would otherwise
  // re-insert on every unrelated re-render). MUST run before any early
  // return (Rules of Hooks) — it previously sat after the loading/empty
  // returns and crashed once a schedule resolved ("rendered more hooks").
  const addInFlightRef = useRef(false);
  useEffect(() => {
    if (!id || !pickedAssetId) return;
    if (pickedAsset === undefined) return; // still loading the asset
    if (addInFlightRef.current) return;
    if (pickedAsset === null) {
      // Asset vanished between pick and read — clear and bail honestly.
      setPickedAssetId(null);
      return;
    }
    addInFlightRef.current = true;
    const asset: any = pickedAsset;
    void (async () => {
      try {
        await addItemM.run({
          scheduleContainerId: id as any,
          assetId: pickedAssetId,
          title: asset.title ?? 'Sin título',
          durationMs: asset.durationMs ?? 0,
          startTime: 0,
          sourceUrl: asset.playbackUrl ?? undefined,
          // NOTE: `thumbnailUrl` is deliberately NOT sent (NMW-454). Nothing in
          // this stack generates poster art, and the only writer of
          // `assets.thumbnailUrl` is the token-gated MRSS ingest, so the value
          // read here was always `undefined` and merely persisted an empty
          // column. The backend arg stays optional/additive — if MRSS assets
          // ever land, re-wiring is a one-line change.
        });
        await recalcM.run({
          scheduleContainerId: id as any,
          baseStartTime: 0,
        });
      } catch {
        // honest wrapper already toasted the failure.
      } finally {
        addInFlightRef.current = false;
        setPickedAssetId(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedAsset, pickedAssetId, id]);

  // Drag sensors — Pointer covers mouse + touch (touch-drag starts from the
  // grip handle, which sets `touch-action: none`, so the rest of the row
  // still scrolls on mobile). Keyboard sensor keeps the list operable
  // without a pointer (a11y): focus a handle, Space to lift, arrows to move.
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Keep the optimistic order in lockstep with the live item set while in
  // reorder mode: drop ids that vanished, append ids that appeared (e.g. an
  // add/delete from another tab), preserving the user's current arrangement.
  useEffect(() => {
    if (!reorderMode) return;
    const ids = items.map((it: any) => it._id as string);
    setLocalOrder((prev) => {
      if (!prev) return ids;
      const kept = prev.filter((pid) => ids.includes(pid));
      const added = ids.filter((iid) => !kept.includes(iid));
      const next = [...kept, ...added];
      const unchanged =
        next.length === prev.length && next.every((v, i) => v === prev[i]);
      return unchanged ? prev : next;
    });
  }, [reorderMode, items]);

  // ──────────────────────────────────────────────────────────────────
  // Loading / honest-empty handling.
  // ──────────────────────────────────────────────────────────────────
  if (id && !validId) {
    // `sc_new` is the prototype's create-placeholder; anything else here is
    // a malformed/hand-typed id. Honest stub either way — no fabricated doc.
    return (
      <EmptyState
        icon="calendar"
        title={id === 'sc_new' ? 'Aún no disponible' : 'Programación no encontrada'}
        body={
          id === 'sc_new'
            ? 'La creación de programaciones desde esta pantalla todavía no está conectada. Mientras tanto, duplica una programación existente desde la lista.'
            : 'El identificador de la programación no es válido.'
        }
      />
    );
  }
  if (id && scheduleDoc === undefined) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Skeleton h={60} />
        <Skeleton h={260} />
      </div>
    );
  }
  if (id && scheduleDoc === null) {
    return (
      <EmptyState
        icon="calendar"
        title="Programación no encontrada"
        body="No pudimos cargar esta programación. Puede haber sido eliminada."
      />
    );
  }

  // ──────────────────────────────────────────────────────────────────
  // Real call-sites (FR-014 Twin #1 binds api.schedules.markAsReady).
  // ──────────────────────────────────────────────────────────────────
  const handleMarkAsReady = () => {
    if (!id) return;
    void markAsReadyM.run({ scheduleId: id as any });
  };
  const handleDuplicate = () => {
    if (!id) return;
    void duplicateScheduleM.run({ scheduleId: id as any });
  };
  // On success both navigate to the channel so the operator SEES the result
  // (the channel goes "En vivo" / shows the scheduled block) — closing the
  // dialog with no feedback read as "nothing happened".
  const handleScheduleForFuture = async () => {
    if (!id || !selectedChannel || !futureDate) return;
    const startTime = new Date(futureDate).getTime();
    if (!Number.isFinite(startTime)) return;
    try {
      await scheduleForFutureM.run({
        scheduleId: id as any,
        channelId: selectedChannel as any,
        startTime,
      });
      navigate('/channels/' + selectedChannel);
    } catch {
      // wrapper surfaced the error toast; stay on the editor
    }
  };
  const handlePublishNow = async () => {
    if (!id || !selectedChannel) return;
    try {
      await publishImmediatelyM.run({
        scheduleId: id as any,
        channelId: selectedChannel as any,
      });
      navigate('/channels/' + selectedChannel);
    } catch {
      // wrapper surfaced the error toast; stay on the editor
    }
  };

  const handleConfirmDelete = () => {
    if (!id || !deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    void (async () => {
      try {
        await deleteItemM.run({ scheduleItemId: target._id as any });
        await recalcM.run({
          scheduleContainerId: id as any,
          baseStartTime: 0,
        });
      } catch {
        // honest wrapper already toasted the failure.
      }
    })();
  };

  // NMW-418 Phase 4 — repin the open item to a chosen ready rendition. Closes
  // the Modal only on success; keeps it open on error so the operator can pick
  // another / retry (the honest wrapper already toasted the throw).
  const handleRepin = (transcodeId: string) => {
    if (!versionTarget) return;
    const scheduleItemId = versionTarget._id;
    void (async () => {
      try {
        await repinM.run({
          scheduleItemId: scheduleItemId as any,
          transcodeId: transcodeId as any,
        });
        setVersionTarget(null); // success — close the picker
      } catch {
        // honest wrapper already toasted; leave the Modal open for retry.
      }
    })();
  };

  const enterReorder = () => {
    setLocalOrder(items.map((it: any) => it._id as string));
    setReorderMode(true);
  };
  const exitReorder = () => {
    setReorderMode(false);
    setLocalOrder(null);
  };
  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!id || !over || active.id === over.id) return;
    const base = localOrder ?? items.map((it: any) => it._id as string);
    const oldIndex = base.indexOf(active.id as string);
    const newIndex = base.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    const prev = base;
    const next = arrayMove(base, oldIndex, newIndex);
    setLocalOrder(next); // optimistic — row stays where it was dropped
    void (async () => {
      try {
        await reorderM.run({
          scheduleContainerId: id as any,
          itemIds: next as any,
        });
        await recalcM.run({
          scheduleContainerId: id as any,
          baseStartTime: 0,
        });
      } catch {
        // honest wrapper already toasted — revert the optimistic move.
        setLocalOrder(prev);
      }
    })();
  };

  const isBusy =
    markAsReadyM.loading ||
    duplicateScheduleM.loading ||
    scheduleForFutureM.loading ||
    publishImmediatelyM.loading ||
    addItemM.loading ||
    deleteItemM.loading ||
    recalcM.loading ||
    reorderM.loading ||
    repinM.loading;

  // Working order for the reorder view: map the optimistic id list back to
  // the live item objects (filtering any that disappeared). Falls back to the
  // query order. Plain computation (not a hook) — safe after the early returns.
  const itemsById: Record<string, any> = {};
  for (const it of items) itemsById[it._id] = it;
  const orderedItems =
    reorderMode && localOrder
      ? localOrder.map((oid) => itemsById[oid]).filter(Boolean)
      : items;
  const orderedIds = orderedItems.map((it: any) => it._id as string);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <Breadcrumbs
            items={[
              { label: 'Programaciones', href: '#/schedules' },
              { label: name },
            ]}
          />
          <h1
            style={{
              margin: '10px 0 0',
              fontFamily: 'var(--font-display)',
              fontSize: 32,
              fontWeight: 400,
              letterSpacing: '-0.4px',
            }}
          >
            {name}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <AccessGate perm="schedule_publish">
            <Btn
              variant="ghost"
              icon="copy"
              onClick={handleDuplicate}
              disabled={!id || isBusy}
            >
              Duplicar
            </Btn>
          </AccessGate>
          <AccessGate perm="schedule_publish">
            <Btn
              variant="ghost"
              icon="checkCircle"
              onClick={handleMarkAsReady}
              disabled={!id || isBusy}
            >
              Marcar como lista
            </Btn>
          </AccessGate>
          <Btn
            variant="ghost"
            icon={validationIssueCount > 0 ? 'alertTriangle' : 'checkCircle'}
            tone={validationIssueCount > 0 ? 'warn' : undefined}
            onClick={() => setValOpen(true)}
            disabled={!id}
          >
            {validationIssueCount > 0
              ? `Validar (${validationIssueCount})`
              : 'Validar'}
          </Btn>
          <AccessGate perm="schedule_publish">
            <Btn
              variant="solid"
              icon="send"
              onClick={() => setPubOpen(true)}
              disabled={!id || isBusy}
            >
              Publicar
            </Btn>
          </AccessGate>
        </div>
      </div>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard
          label="Duración total"
          value={fmtDur(totalMs)}
          icon="clock"
        />
        <StatCard
          label="Elementos"
          value={items.length}
          hint="en la parrilla"
          icon="list"
        />
        <StatCard
          label="Estado"
          value={scheduleDoc?.status ?? '—'}
          icon="info"
        />
        <StatCard
          label="Canal"
          value={scheduleDoc?.channelId ? 'Asignado' : 'Sin asignar'}
          icon="tv"
        />
      </div>
      {scheduleDoc && !scheduleDoc.channelId && (
        <Panel pad={16} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14 }}>
              Esta programación no está asignada a un canal. Asígnale uno para poder añadir contenido.
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Select
                value={assignChannelId}
                onChange={setAssignChannelId}
                options={[
                  { value: '', label: 'Selecciona un canal…' },
                  ...(wiredChannels ?? []).map((c) => ({ value: c._id, label: c.name ?? c.slug ?? c._id })),
                ]}
              />
              <Btn
                variant="solid"
                size="sm"
                loading={assignChannelM.loading}
                disabled={!assignChannelId}
                onClick={() => {
                  if (id && assignChannelId) void assignChannelM.run({ scheduleId: id as any, channelId: assignChannelId as any });
                }}
              >
                Asignar canal
              </Btn>
            </div>
          </div>
        </Panel>
      )}
      {/* Content picker docked to the RIGHT of the timeline — the side the
          "Añadir contenido" button lives on (it replaced the old modal
          dialog): the operator keeps the parrilla in view and adds elements
          one button-click at a time. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <Panel
        style={{ flex: 1, minWidth: 0 }}
        title="Línea de tiempo"
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>
              Total: {fmtDur(totalMs)} · {items.length} elementos
            </span>
            {items.length > 1 && (
              <AccessGate perm="schedule_create">
                <Btn
                  variant={reorderMode ? 'solid' : 'ghost'}
                  size="sm"
                  icon={reorderMode ? 'check' : 'list'}
                  onClick={reorderMode ? exitReorder : enterReorder}
                  disabled={!id}
                >
                  {reorderMode ? 'Listo' : 'Reordenar'}
                </Btn>
              </AccessGate>
            )}
            {!reorderMode && (
              <AccessGate perm="schedule_create">
                <Btn
                  variant={pickerOpen ? 'ghost' : 'solid'}
                  size="sm"
                  icon={pickerOpen ? 'x' : 'plus'}
                  onClick={() => setPickerOpen((v) => !v)}
                  disabled={!id || !scheduleDoc?.channelId}
                  title={!scheduleDoc?.channelId ? 'Asigna un canal a esta programación antes de añadir contenido' : undefined}
                >
                  {pickerOpen ? 'Cerrar panel' : 'Añadir contenido'}
                </Btn>
              </AccessGate>
            )}
          </div>
        }
      >
        {items.length === 0 ? (
          <EmptyState
            icon="calendar"
            title="Sin elementos"
            body="Esta programación aún no tiene elementos."
            action={
              <AccessGate perm="schedule_create">
                <Btn
                  variant="solid"
                  icon="plus"
                  onClick={() => setPickerOpen(true)}
                  disabled={!id || !scheduleDoc?.channelId}
                  title={!scheduleDoc?.channelId ? 'Asigna un canal a esta programación antes de añadir contenido' : undefined}
                >
                  Añadir contenido
                </Btn>
              </AccessGate>
            }
          />
        ) : reorderMode ? (
          <div>
            <p
              style={{
                margin: '0 0 8px',
                fontSize: 13,
                color: 'var(--muted-slate)',
              }}
            >
              Arrastra cada elemento por el asa para reordenarlo. Los horarios
              se recalculan automáticamente.
            </p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedIds}
                strategy={verticalListSortingStrategy}
              >
                <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                  {orderedItems.map((it: any, idx: number) => (
                    <SortableItemRow
                      key={it._id}
                      it={it}
                      displayIndex={idx}
                      isLast={idx === orderedItems.length - 1}
                      warn={warnReasonFor(it._id)}
                      majority={outlierById.get(it._id)?.outlier?.majority}
                      busy={isBusy}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        ) : (
          <div
            ref={timelineScrollRef}
            style={{ maxHeight: 480, overflowY: 'auto' }}
          >
            <div
              style={{
                position: 'relative',
                height: rowVirtualizer.getTotalSize(),
                width: '100%',
              }}
            >
              {virtualRows.map((vr) => {
                const it: any = items[vr.index] ?? {};
                const i = vr.index;
                return (
                  <div
                    key={it._id ?? i}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      transform: `translateY(${vr.start}px)`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 0',
                      // The row pitch is estimateSize (no measureElement), so
                      // the row must occupy exactly that height — otherwise a
                      // taller row (mismatch button / AccessGate skeleton)
                      // paints over the next one.
                      height: TIMELINE_ROW_H,
                      boxSizing: 'border-box',
                      borderBottom:
                        i < items.length - 1
                          ? '1px solid var(--lightest-gray)'
                          : 'none',
                    }}
                  >
                    <span
                      className="t-mono"
                      style={{
                        fontSize: 12,
                        color: 'var(--muted-slate)',
                        width: 56,
                      }}
                    >
                      #{(it.sequenceNumber ?? i) + 1}
                    </span>
                    <ItemTitle it={it} />
                    {outlierById.has(it._id) && (
                      <FormatMismatchCell
                        scheduleItemId={it._id}
                        assetId={it.assetId}
                        reason={
                          warnReasonFor(it._id) ??
                          'Perfil de video inconsistente'
                        }
                        majority={outlierById.get(it._id)?.outlier?.majority ?? {}}
                        disabled={isBusy}
                      />
                    )}
                    <Pill
                      tone={outlierById.has(it._id) ? 'warn' : 'neutral'}
                      size="sm"
                    >
                      {fmtProfile(it)}
                    </Pill>
                    <Pill tone="neutral" size="sm">
                      {fmtDur(it.durationMs ?? 0)}
                    </Pill>
                    {it.status !== 'playing' && it.status !== 'live_active' && (
                      <AccessGate perm="schedule_create">
                        <button
                          type="button"
                          aria-label="Cambiar versión"
                          title="Cambiar versión"
                          onClick={() =>
                            setVersionTarget({
                              _id: it._id,
                              assetId: it.assetId,
                              transcodeId: it.transcodeId,
                              title: it.title ?? 'Sin título',
                            })
                          }
                          disabled={isBusy || !it.assetId}
                          style={{
                            border: 'none',
                            background: 'none',
                            cursor: isBusy ? 'not-allowed' : 'pointer',
                            color: 'var(--muted-slate)',
                            display: 'flex',
                          }}
                        >
                          <Icon name="layers" size={15} />
                        </button>
                      </AccessGate>
                    )}
                    {it.status !== 'playing' && it.status !== 'live_active' && (
                      <AccessGate perm="schedule_create">
                        <button
                          aria-label="Eliminar elemento"
                          onClick={() =>
                            setDeleteTarget({
                              _id: it._id,
                              title: it.title ?? 'Sin título',
                            })
                          }
                          disabled={isBusy}
                          style={{
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            color: 'var(--muted-slate)',
                            display: 'flex',
                          }}
                        >
                          <Icon name="trash" size={15} />
                        </button>
                      </AccessGate>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </Panel>
        <AddContentSidebar
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onAdd={(assetId) => setPickedAssetId(assetId)}
          addingAssetId={pickedAssetId}
          busy={isBusy}
          disabledReason={
            !id
              ? 'Programación no disponible.'
              : !scheduleDoc?.channelId
                ? 'Asigna un canal a esta programación antes de añadir contenido.'
                : undefined
          }
        />
      </div>

      <Modal
        open={pubOpen}
        onClose={() => setPubOpen(false)}
        title="Publicar programación"
        width={560}
        footer={
          <>
            <Btn variant="ghost" onClick={() => setPubOpen(false)}>
              Cancelar
            </Btn>
            {scheduleLater ? (
              <Btn
                variant="solid"
                onClick={() => {
                  void handleScheduleForFuture();
                  setPubOpen(false);
                }}
                disabled={!selectedChannel || !futureDate || isBusy}
              >
                Agendar
              </Btn>
            ) : (
              <Btn
                variant="solid"
                onClick={() => {
                  void handlePublishNow();
                  setPubOpen(false);
                }}
                disabled={!selectedChannel || isBusy}
              >
                Salir al aire ahora
              </Btn>
            )}
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field
            label="Canal"
            hint={scheduleDoc?.channelId
              ? 'Ya asignado a esta programación.'
              : 'El canal que transmitirá esta programación.'}
          >
            <Select
              value={selectedChannel}
              onChange={setSelectedChannel}
              full
              options={[
                { value: '', label: 'Selecciona un canal…' },
                ...(wiredChannels ?? []).map((c) => ({
                  value: c._id,
                  label: c.name ?? c.slug ?? c._id,
                })),
              ]}
            />
          </Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--slate)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={scheduleLater}
              onChange={(e) => setScheduleLater(e.currentTarget.checked)}
            />
            Programar para una fecha y hora futura
          </label>
          {scheduleLater && (
            <Field label="Fecha y hora de inicio">
              <Input type="datetime-local" value={futureDate} onChange={setFutureDate} />
            </Field>
          )}
        </div>
      </Modal>

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar elemento"
        danger
        body={
          deleteTarget
            ? `Se eliminará "${deleteTarget.title}" de la programación. Los horarios se recalcularán.`
            : ''
        }
        confirmLabel="Eliminar"
        onConfirm={handleConfirmDelete}
      />

      {/* NMW-418 Phase 4 — per-item rendition picker. One instance, fed by
          versionTarget; the getAssetTranscodes query is scoped to the open
          item so only one runs at a time. */}
      <Modal
        open={!!versionTarget}
        onClose={() => setVersionTarget(null)}
        title="Cambiar versión"
        width={520}
        footer={
          <Btn variant="ghost" onClick={() => setVersionTarget(null)}>
            Cerrar
          </Btn>
        }
      >
        {versionTarget && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--muted-slate)' }}>
              Selecciona una versión lista de{' '}
              <strong>{versionTarget.title}</strong> para reasignar este
              elemento. Los horarios se recalculan automáticamente.
            </p>
            {versionData === undefined ? (
              // LOADING — undefined query result.
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>
                  Cargando versiones…
                </span>
                <Skeleton h={48} />
                <Skeleton h={48} />
              </div>
            ) : versionData.renditions.length <= 1 ? (
              // EMPTY — no alternative to switch to (only the current rendition).
              <EmptyState
                icon="layers"
                title="Sin otras versiones"
                body="Este elemento no tiene otras versiones disponibles para asignar."
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {versionData.renditions.map((r: any) => {
                  const isPinned = r._id === versionTarget.transcodeId;
                  const isActive = r._id === versionData.activeTranscodeId;
                  const isReady = r.status === 'ready';
                  const selectable = isReady && !isPinned;
                  return (
                    <button
                      key={r._id}
                      type="button"
                      disabled={!selectable || isBusy}
                      onClick={() => handleRepin(r._id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        width: '100%',
                        textAlign: 'left',
                        border: '1px solid var(--lightest-gray)',
                        borderRadius: 10,
                        background: 'none',
                        padding: '10px 12px',
                        cursor: selectable && !isBusy ? 'pointer' : 'default',
                        opacity: selectable ? 1 : 0.6,
                      }}
                    >
                      <span style={{ flex: 1, fontSize: 14 }}>
                        {fmtRendition(r.profile)}
                        <span style={{ color: 'var(--muted-slate)' }}>
                          {' · '}
                          {KIND_LABEL[r.kind] ?? r.kind}
                        </span>
                      </span>
                      {!isReady && (
                        <Pill
                          tone={r.status === 'failed' ? 'bad' : 'warn'}
                          size="sm"
                        >
                          {r.status === 'failed'
                            ? 'fallida'
                            : r.status === 'processing'
                              ? 'procesando'
                              : 'pendiente'}
                        </Pill>
                      )}
                      {isActive && (
                        <Pill tone="good" size="sm">
                          Activa
                        </Pill>
                      )}
                      {isPinned && (
                        <Pill tone="info" size="sm">
                          Fijada
                        </Pill>
                      )}
                      {selectable && (
                        <Icon
                          name="arrowRight"
                          size={15}
                          color="var(--muted-slate)"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* NMW-416 — full validation drawer (gaps, ad-time, profile mismatch). */}
      <ScheduleValidationDrawer
        open={valOpen}
        onClose={() => setValOpen(false)}
        scheduleId={id}
      />
    </div>
  );
}

/**
 * Timeline row title, linked to the underlying media asset.
 *
 * A schedule row already carries `assetId` (it feeds the version picker), but
 * the timeline never exposed it — inspecting the media behind an item meant
 * searching for it by name in the library. The title is now an anchor to
 * `/library/:assetId` (the ContentDetail route), rendered as a real `href` so
 * middle-click / cmd-click open a new tab, with the click intercepted for the
 * in-app router otherwise.
 *
 * Honest about what it cannot link: `schedule.assetId` is a plain STRING and
 * legacy / sentinel rows (e.g. the `__live__` placeholder) hold values that are
 * not asset ids. Those fall back to the previous plain-text title rather than
 * producing a link that 404s.
 */
function ItemTitle({ it }: { it: any }) {
  const label: string = it?.title ?? 'Sin título';
  const assetId: string | undefined = it?.assetId;
  const linkable = !!assetId && /^[a-z0-9]{20,}$/.test(assetId);
  const base = {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  };
  if (!linkable) {
    return <span style={base}>{label}</span>;
  }
  const href = `/library/${assetId}`;
  return (
    <a
      href={href}
      title={`Abrir "${label}" en la biblioteca`}
      onClick={(e) => {
        // Leave modified clicks to the browser (new tab / new window).
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        navigate(href);
      }}
      style={{
        ...base,
        display: 'block',
        color: 'inherit',
        textDecoration: 'underline',
        textDecorationColor: 'var(--lightest-gray)',
        textUnderlineOffset: 3,
      }}
    >
      {label}
    </a>
  );
}

/**
 * A single draggable timeline row (reorder mode only). The drag listeners are
 * bound to the grip handle alone, not the whole row, so on touch devices the
 * list still scrolls — only the handle initiates a drag.
 */
function SortableItemRow({
  it,
  displayIndex,
  isLast,
  warn,
  majority,
  busy,
}: {
  it: any;
  displayIndex: number;
  isLast: boolean;
  warn?: string | null;
  /** The playlist's majority profile — the conversion target for this row. */
  majority?: any;
  busy?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: it._id });
  const isLive = it.status === 'playing' || it.status === 'live_active';
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 8px',
        borderBottom: isLast ? 'none' : '1px solid var(--lightest-gray)',
        background: isDragging ? 'var(--lightest-gray)' : 'transparent',
        borderRadius: isDragging ? 8 : 0,
        position: 'relative',
        zIndex: isDragging ? 1 : 0,
        boxShadow: isDragging ? 'var(--shadow-sm, 0 4px 12px rgba(0,0,0,0.12))' : 'none',
      }}
    >
      <button
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label="Arrastrar para reordenar"
        style={{
          border: 'none',
          background: 'none',
          cursor: isDragging ? 'grabbing' : 'grab',
          color: 'var(--muted-slate)',
          display: 'flex',
          padding: 4,
          touchAction: 'none',
        }}
      >
        <Icon name="menu" size={16} />
      </button>
      <span
        className="t-mono"
        style={{ fontSize: 12, color: 'var(--muted-slate)', width: 32 }}
      >
        #{displayIndex + 1}
      </span>
      <ItemTitle it={it} />
      {isLive && (
        <span style={{ fontSize: 12, color: 'var(--cta)', fontWeight: 500 }}>
          en vivo
        </span>
      )}
      {warn && (
        <FormatMismatchCell
          scheduleItemId={it._id}
          assetId={it.assetId}
          reason={warn}
          majority={majority ?? {}}
          // A live/playing row must not be repinned or re-encoded out from
          // under the channel — mirrors the row's own live guard.
          disabled={busy || isLive}
        />
      )}
      <Pill tone={warn ? 'warn' : 'neutral'} size="sm">
        {it.resolution
          ? `${it.resolution.height}x${it.resolution.width}@${typeof it.frameRate === 'number' ? it.frameRate : 'null'}`
          : 'Sin perfil'}
      </Pill>
      <Pill tone="neutral" size="sm">
        {fmtDur(it.durationMs ?? 0)}
      </Pill>
    </div>
  );
}

export default SchedulesEditor;
